import React from "react"
import { GraduationCap, Building2, Code2, ExternalLink } from "lucide-react"
import Section from "../common/Section"

const About = ({ t, language }) => {
  const showManduSection = language === "ko" || language === "en"

  const achievements = [
    {
      icon: <GraduationCap size={32} className="text-green-500" />,
      stat: "500+",
      label: t.achievementLabels[0],
    },
    {
      icon: <Building2 size={32} className="text-blue-500" />,
      stat: "15+",
      label: t.achievementLabels[1],
    },
    {
      icon: <Code2 size={32} className="text-purple-500" />,
      stat: "30+",
      label: t.achievementLabels[2],
    },
  ]

  const projectTechStacks = [
    ["React", "Node.js", "Claude API"],
    ["React", "Firebase", "WebSocket"],
    ["Flutter", "Dart", "REST API"],
  ]

  const projects = t.projects.map((project, index) => ({
    ...project,
    tech: projectTechStacks[index] || [],
  }))

  return (
    <Section id="about" className="bg-white text-gray-800">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
        {t.sectionTitle}
      </h2>
      <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
        {t.sectionSubtitle}
      </p>

      {/* 실적 통계 */}
      <div className="grid grid-cols-3 gap-6 mb-16">
        {achievements.map((item, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-center mb-3">{item.icon}</div>
            <div className="text-3xl font-bold text-gray-800 mb-1">
              {item.stat}
            </div>
            <div className="text-sm text-gray-600">{item.label}</div>
          </div>
        ))}
      </div>

      {/* 소개 및 이미지 */}
      <div className="grid md:grid-cols-2 gap-10 items-center mb-16">
        <div>
          <img
            src="/banner.jpg"
            alt={t.bannerAlt}
            className="rounded-lg shadow-xl"
            onError={(e) => {
              e.target.onerror = null
              e.target.src =
                "https://placehold.co/600x400/CBD5E0/4A5568?text=Image+Error&font=Inter"
            }}
          />
        </div>
        <div className="text-lg text-gray-700 space-y-4">
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
            {t.introTitle}
          </h3>
          <p>{t.introParagraph1}</p>
          <p>{t.introParagraph2}</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mt-4">
            {t.educationPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      </div>

      {showManduSection && (
        <>
          {/* 만두쌤의 코딩 한 꼬집 - 모바일 */}
          <div className="mb-16 md:hidden bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl p-6 border border-orange-100">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <img
                  src="/mandu_logo.png"
                  alt={t.mandu.logoAlt}
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {t.mandu.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {t.mandu.mobileSubtitle}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-500">
                {t.mandu.features.join(" · ")}
              </p>
              <a
                href="https://mandu1.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg font-medium transition-colors w-full"
              >
                {t.mandu.visit}
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* 만두쌤의 코딩 한 꼬집 - 데스크톱 */}
          <div className="mb-16 hidden md:block bg-gradient-to-br from-orange-50 via-white to-yellow-50 rounded-3xl p-8 border border-orange-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center p-2">
                  <img
                    src="/mandu_logo.png"
                    alt={t.mandu.logoAlt}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-tight">
                    {t.mandu.title}
                  </h3>
                  <p className="text-gray-500 mt-1">
                    {t.mandu.desktopSubtitle}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    {t.mandu.features[0]}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    {t.mandu.features[1]}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                    {t.mandu.features[2]}
                  </span>
                </div>
                <a
                  href="https://mandu1.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-medium transition-all hover:scale-105"
                >
                  {t.mandu.visit}
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 프로젝트 실적 */}
      <div>
        <h3 className="text-2xl font-semibold text-center text-gray-900 mb-8">
          {t.projectsTitle}
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          {projects.map((project, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all hover:border-green-300"
            >
              <div className="text-sm font-medium text-green-600 mb-2">
                {project.company}
              </div>
              <p className="text-gray-700 mb-4">{project.description}</p>
              <div className="flex flex-wrap gap-2">
                {project.tech.map((tech, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

export default About
